import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} muteToggleButton - The bottom-right mute/unmute icon button
 * @property {HTMLElement} centerMuteButton - The centered unmute overlay button
 * @property {HTMLElement} playToggleButton - The bottom-right play/pause icon button
 * @property {HTMLElement} videoProgress - The progress bar container
 * @property {HTMLElement} videoStateIndicator - The centered play/pause flash indicator
 * @property {HTMLElement} videoProgressFill - The progress bar fill element
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  /** @type {boolean} */
  isMuted = true;

  /** @type {boolean} */
  #isScrubbing = false;

  /** @type {boolean} Only flash the state indicator when set by a user interaction */
  #userInitiated = false;

  /** @type {boolean} */
  #centerMuteDismissed = false;

  /** @type {boolean} True once first 'playing' event has fired */
  #hasRendered = false;

  /** @type {AbortController} Recreated on every connect — Swiper's loop mode
   * moves slide DOM nodes between positions, which triggers disconnect →
   * reconnect on custom elements. Reusing an already-aborted controller
   * silently drops every listener registered in the second connect. */
  #abortController = new AbortController();

  /** @type {AbortController | null} Mini-video listener lifetime. Distinct
   * from #abortController so that hydrating the full video can detach the
   * mini's listeners (preventing its 'pause'/'volumechange'/'timeupdate'
   * events from competing with the full's during the fade) without aborting
   * everything else on the host element. Null when there is no mini. */
  #miniListenersController = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }
    const signal = this.#abortController.signal;
    // The "another video started, pause me" cross-page behavior should only
    // fire for user-initiated playback (a product gallery click, a deferred
    // poster click, etc.). Autoplay videos — hero, card carousel, slideshow
    // autoplay — should coexist: starting the carousel mustn't pause the
    // hero. So we filter out events whose source is another autoplay video.
    document.addEventListener(ThemeEvents.mediaStartedPlaying, (event) => {
      const source = /** @type {HTMLElement | null} */ (event.target);
      if (this.hasAttribute('autoplay') && source?.hasAttribute?.('autoplay')) return;
      this.pauseMedia();
    }, { signal });
    // Audio focus: only one video plays unmuted at a time across the page.
    // When another deferred-media announces it's now unmuted+playing, this
    // one mutes itself (we mute rather than pause so the visual continues
    // — pausing would feel like the click broke unrelated playback).
    // Same-group exemption: videos inside the same card-carousel or
    // media-gallery already coordinate via data-unmuted, so the cross-page
    // listener skips them to avoid fighting the local mute-sync.
    document.addEventListener('media:audio-focus', (event) => {
      const source = /** @type {HTMLElement | null} */ (
        /** @type {CustomEvent} */ (event).detail?.source
      );
      if (!source || source === this) return;
      const myGroup = this.closest('card-carousel, media-gallery');
      const sourceGroup = source.closest?.('card-carousel, media-gallery');
      if (myGroup && myGroup === sourceGroup) return;
      if (!this.isMuted) this.#mute(false);
    }, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    if (this.hasAttribute('autoplay')) {
      this.isPlaying = false;

      if (!this.hasAttribute('data-background-video')) {
        this.addEventListener('click', this.#handleVideoClick.bind(this), { signal });
        this.#initProgressBar(signal);
        this.#initCursorFollow(this.refs.centerMuteButton, signal);
      }

      // Pick the right autoplay strategy based on the host context.
      // - Inside <card-carousel>: that element manages playback explicitly
      //   (see card-carousel.js). We do nothing here so the carousel's
      //   playMedia/pauseMedia calls are the single source of truth.
      // - Inside a <slideshow-component> slide: react to slideshow:select.
      // - Otherwise: lazy-start when the element enters the viewport.
      if (this.closest('card-carousel')) {
        // No-op: card-carousel.js orchestrates this video.
      } else if (this.closest('slideshow-slide')) {
        this.#initSlideshowAutoplay(signal);
      } else {
        this.#initViewportAutoplay(signal);
      }
    } else if (this.hasAttribute('data-custom-controls')) {
      // Non-autoplay with custom JS controls (e.g. product gallery videos).
      // Controls start hidden and appear after the user clicks the poster play button.
      this.isMuted = false;
      this.addEventListener('click', this.#handleVideoClick.bind(this), { signal });
      this.#initProgressBar(signal);
      this.#initCustomControlsReveal(signal);
      this.#initSlideshowPause(signal);
    }

    if (!this.hasAttribute('autoplay') && this.refs.deferredMediaPlayButton) {
      const posterIcon = this.refs.deferredMediaPlayButton.querySelector('.deferred-media__poster-icon');
      this.#initCursorFollow(posterIcon, signal);
    }

    this.#observeVideoElement(signal);
  }

  /**
   * Uses IntersectionObserver to defer video loading and playback until
   * the element enters the viewport. Prevents Safari from buffering
   * off-screen videos and starving image loads of HTTP connections.
   * @param {AbortSignal} signal
   */
  #initViewportAutoplay(signal) {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#startAutoplay();
            observer.disconnect();
          }
        }
      },
      { rootMargin: '200px 0px' }
    );

    observer.observe(this);
    signal.addEventListener('abort', () => observer.disconnect());
  }

  /**
   * Listens for slideshow:select events to play/pause videos inside
   * slideshow slides. Works correctly with Swiper loop mode (clones)
   * because the event provides the real slide index.
   * @param {AbortSignal} signal
   */
  #initSlideshowAutoplay(signal) {
    const slide = this.closest('slideshow-slide');
    const slideshow = this.closest('slideshow-component');
    if (!slide || !slideshow) return;

    let hasStarted = false;

    // Determine this slide's real index from the slides ref array
    const getMyIndex = () => {
      const slides = slideshow.refs?.slides;
      if (!slides) return -1;
      return Array.prototype.indexOf.call(slides, slide);
    };

    const handleSelect = (event) => {
      const myIndex = getMyIndex();
      if (myIndex === -1) return;

      if (event.detail.index === myIndex) {
        if (!hasStarted) {
          hasStarted = true;
          this.#startAutoplay();
        } else {
          this.playMedia();
        }

        // Apply stored mute state from gallery container
        const gallery = this.closest('media-gallery') || this.closest('[data-video-autoplay]');

        if (gallery?.hasAttribute('data-unmuted')) {
          this.#centerMuteDismissed = true;
          this.applyCarouselMuteState(false);
        }
      } else if (hasStarted) {
        this.pauseMedia();
      }
    };

    slideshow.addEventListener('slideshow:select', handleSelect, { signal });

    // If this slide is already the active one on init, start immediately
    const myIndex = getMyIndex();

    if (myIndex >= 0 && slideshow.current === myIndex) {
      hasStarted = true;
      this.#startAutoplay();
    }
  }

  /**
   * Reveals hidden controls and progress bar after the user clicks
   * the poster play button (non-autoplay + disable_controls mode).
   * Watches for the data-media-loaded attribute set by loadContent().
   * @param {AbortSignal} signal
   */
  #initCustomControlsReveal(signal) {
    const reveal = () => {
      const controls = this.querySelector('.video-controls');
      const progress = this.querySelector('.video-progress');
      if (controls) controls.classList.remove('hidden');
      if (progress) progress.classList.remove('hidden');
    };

    // If already loaded, reveal immediately
    if (this.getAttribute('data-media-loaded')) {
      reveal();
      return;
    }

    // Watch for loadContent() setting data-media-loaded
    const observer = new MutationObserver(() => {
      if (this.getAttribute('data-media-loaded')) {
        observer.disconnect();
        reveal();
      }
    });

    observer.observe(this, { attributes: true, attributeFilter: ['data-media-loaded'] });
    signal.addEventListener('abort', () => observer.disconnect());
  }

  /**
   * Pauses a non-autoplay video when the user navigates away from its
   * slide in a slideshow. Listens for slideshow:select events.
   * @param {AbortSignal} signal
   */
  #initSlideshowPause(signal) {
    const slide = this.closest('slideshow-slide');
    const slideshow = this.closest('slideshow-component');
    if (!slide || !slideshow) return;

    const getMyIndex = () => {
      const slides = slideshow.refs?.slides;
      if (!slides) return -1;
      return Array.prototype.indexOf.call(slides, slide);
    };

    slideshow.addEventListener('slideshow:select', (event) => {
      const myIndex = getMyIndex();
      if (myIndex === -1) return;

      if (event.detail.index !== myIndex && this.isPlaying) {
        this.pauseMedia();
      }
    }, { signal });
  }

  /**
   * Starts autoplay: sets preload, triggers play on the video element
   */
  #startAutoplay() {
    /** @type {HTMLVideoElement | null} */
    const video = this.querySelector('video');
    if (video) {
      this.#loadAndPlay(video);
    } else {
      // Video element may not be in the DOM yet (e.g. template-based)
      const observer = new MutationObserver(() => {
        const v = this.querySelector('video');
        if (v) {
          observer.disconnect();
          this.#loadAndPlay(v);
        }
      });
      observer.observe(this, { childList: true, subtree: true });
    }

    // Poster overlay is faded on first 'playing' event by #observeVideoElement
    // so the poster stays visible during buffering instead of revealing a
    // blank video element.

    this.isPlaying = true;
    this.updatePlayPauseHint(true);
  }

  /**
   * Attaches native <video> event listeners that drive UI state.
   * For the mini placeholder, listeners are scoped to a separate
   * AbortController so they can be removed at hydrate time.
   * @param {HTMLVideoElement} video
   */
  #attachVideoListeners(video) {
    const isMini = video.classList.contains('deferred-media__mini-video');
    if (isMini && !this.#miniListenersController) {
      this.#miniListenersController = new AbortController();
    }
    const signal = isMini
      ? /** @type {AbortController} */ (this.#miniListenersController).signal
      : this.#abortController.signal;

    // iOS Safari first-frame paint regression (15.4+): WebKit paints
    // the first 1-2 decoded frames of a <video> at the source's
    // intrinsic pixel dimensions before applying object-fit: cover —
    // a brief zoomed-in flash. Fix: count 2 requestVideoFrameCallback
    // firings before committing the visual reveal. The first frame
    // (potentially bad) is suppressed by CSS that keeps the video at
    // opacity 0 until the host's reveal attribute is set; what shows
    // during the gap is the upstream poster (full video) or the
    // sibling mini placeholder (mini video).
    // https://developer.apple.com/forums/thread/707247
    //
    // Two reveal targets, same mechanism:
    //   - mini  → sets [data-mini-rendered]  (CSS reveals .deferred-media__mini-video)
    //   - full  → sets [data-video-rendered] (CSS reveals deferred-media > video,
    //                                         and adds .deferred-media__playing
    //                                         to fade the poster button)
    if (isMini) {
      const reveal = () => {
        // data-mini-rendered: the mini's own opacity gate (CSS reveals
        // .deferred-media__mini-video when this is present).
        this.setAttribute('data-mini-rendered', '');
        // First time ANY video has rendered on this host: also set the
        // sitewide [data-video-rendered] flag — gates unmute toggle
        // visibility, fades the poster, and is the canonical "video
        // has appeared" signal. Without this set on the mini's reveal,
        // mini-mode carousel cards would never surface their unmute
        // toggle, because the FULL video stays in <template> until the
        // user unmutes. Single-fire via #hasRendered (a later full
        // hydration's reveal short-circuits here, by design).
        if (this.#hasRendered) return;
        this.#hasRendered = true;
        this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
        this.setAttribute('data-video-rendered', '');
        this.removeAttribute('data-show-play-icon');
      };
      if ('requestVideoFrameCallback' in video) {
        let frames = 0;
        const onFrame = () => {
          if (++frames >= 2) reveal();
          else video.requestVideoFrameCallback(onFrame);
        };
        video.requestVideoFrameCallback(onFrame);
      } else {
        video.addEventListener('playing', () => {
          requestAnimationFrame(() => requestAnimationFrame(reveal));
        }, { once: true, signal });
      }
    } else {
      // Full / main video — same 2-frame counter, different target.
      // This used to live in the 'playing' event listener (set
      // data-video-rendered + .deferred-media__playing on first
      // 'playing'), but 'playing' fires before the first frame has
      // composited correctly on iOS Safari, exposing the bad
      // intrinsic-size paint. rVFC firing twice guarantees the second
      // frame has hit the compositor with object-fit applied.
      const reveal = () => {
        // Carousel guard: same Safari spurious-restart concern as the
        // 'playing' listener — Safari may paint a queued frame on a
        // paused inactive slide whose compositor layer is invalidated
        // during a snap. If that happens, don't commit the reveal —
        // #hasRendered must only flip on a legitimate active render.
        const slide = this.closest('.cc-item');
        if (slide && !slide.hasAttribute('data-active')) return;
        // data-full-rendered: ALWAYS set (no #hasRendered guard).
        // This is a separate flag from data-video-rendered — purpose-
        // built so #hydrateFullVideo's mini-fade trigger can wait for
        // "the FULL is composed correctly" specifically. In mini-mode
        // the mini's reveal already set data-video-rendered when the
        // mini first appeared, so observing data-video-rendered for
        // the mini-fade would fire before the full has composed.
        // data-full-rendered fires only on the full's second rVFC.
        this.setAttribute('data-full-rendered', '');
        if (this.#hasRendered) return;
        this.#hasRendered = true;
        this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
        this.setAttribute('data-video-rendered', '');
        // Clear the centered play icon at the SAME moment the video
        // becomes visible and the unmute toggle is allowed to appear.
        // For first-time render this is the canonical "click → first
        // frame → unmute toggle" handoff. For resume-from-pause this
        // is a no-op (data-video-rendered already set, #hasRendered
        // short-circuit hits) — that path's clear lives in 'playing'.
        this.removeAttribute('data-show-play-icon');
      };
      if ('requestVideoFrameCallback' in video) {
        let frames = 0;
        const onFrame = () => {
          if (++frames >= 2) reveal();
          else video.requestVideoFrameCallback(onFrame);
        };
        video.requestVideoFrameCallback(onFrame);
      } else {
        video.addEventListener('playing', () => {
          requestAnimationFrame(() => requestAnimationFrame(reveal));
        }, { once: true, signal });
      }
    }

    video.addEventListener('play', () => {
      this.isPlaying = true;
      this.updatePlayPauseHint(true);
      this.#flashStateIndicator(true);
    }, { signal });
    // First time the video actually renders frames: fade out the poster
    // overlay. Using 'playing' (not 'play' or 'canplay') ensures the
    // first frame is decoded and visible, so the swap is seamless.
    // 'playing' also fires after recovery from a mid-playback stall, so
    // we use it to clear the buffering attribute the spinner is bound to.
    //
    // data-show-play-icon removal lives here (not in 'play') so the
    // centered state-indicator stays visible from click → first-frame-
    // decoded, matching when the unmute toggle appears for a clean
    // visual handoff. 'play' fires within ~10ms of video.play() resolving
    // — too fast for the user to register click feedback if we hide the
    // icon then. data-paused (semantic "video element is paused") clears
    // here too as it always has.
    video.addEventListener('playing', () => {
      this.removeAttribute('data-video-buffering');

      // Safari spurious-restart guard. WebKit re-attempts autoplay
      // on a paused <video> with the [autoplay] attribute whenever
      // its compositor layer is invalidated — which happens on every
      // Swiper snap (parent .cc-item__card transform animation forces
      // Safari to recompose every hydrated descendant's layers).
      // Without this guard, every previously-active inactive slide's
      // video plays a frame on every slide change, fires 'playing',
      // clears [data-show-play-icon], and the icon flashes off then
      // back on once #applyVisualEffects re-sets it the next frame.
      // Stripping the autoplay attribute here breaks the loop —
      // playMedia restores it when the slide becomes active again.
      const slide = this.closest('.cc-item');
      if (slide && !slide.hasAttribute('data-active')) {
        video.removeAttribute('autoplay');
        video.pause();
        return;
      }

      this.removeAttribute('data-paused');
      // Resume-from-pause: data-video-rendered is already set from the
      // first activation, so the rVFC reveal handler's #hasRendered
      // guard would short-circuit and never re-clear data-show-play-icon.
      // Clear it here so the resume path works without waiting for
      // (and never reaching) a fresh rVFC count. First-time-render path
      // clears it inside the rVFC reveal so the icon disappears in
      // lockstep with the poster fade — see the !isMini branch in
      // #attachVideoListeners.
      if (this.hasAttribute('data-video-rendered')) {
        this.removeAttribute('data-show-play-icon');
      }
      // data-video-rendered + .deferred-media__playing now live in the
      // rVFC reveal handler in #attachVideoListeners (gated on the
      // SECOND composited frame to skip iOS Safari's intrinsic-size
      // first-frame paint). Don't set them here.
    }, { signal });
    // 'waiting' fires when playback stops because the next frame isn't
    // available yet — the canonical buffering signal. Show the spinner.
    video.addEventListener('waiting', () => {
      this.setAttribute('data-video-buffering', '');
    }, { signal });
    // We deliberately do NOT listen to 'stalled' to retry — calling
    // video.load() during an in-flight Range request aborts it and
    // forces the browser to start over, which compounds CDN slowness
    // instead of recovering from it. The browser already retries on
    // its own. The visible loading spinner gives the user feedback
    // when the wait is genuinely long.
    video.addEventListener('pause', () => {
      this.isPlaying = false;
      // data-paused: semantic "the underlying <video> element is paused".
      // Drives the non-autoplay [data-paused] poster-button resume UX
      // (see snippets/video.liquid). Kept unchanged from the prior
      // contract so non-autoplay flows still work the same.
      this.setAttribute('data-paused', '');
      // data-show-play-icon: presentation-only "show the centered play
      // icon now". For autoplay videos this is what makes the
      // .video-state-indicator visible (single CSS rule). On pause we
      // set it so the user has a click-to-resume affordance. Carousel
      // siblings paused via #syncActiveCarouselVideo also flip through
      // here, but card-carousel.js already set the same attribute on
      // them in #applyVisualEffects — idempotent, no flicker.
      // The hydrate path detaches mini listeners BEFORE calling
      // mini.pause(), so swap-related pauses don't fire this handler —
      // only true user-pause / carousel-pause does.
      this.setAttribute('data-show-play-icon', '');
      this.updatePlayPauseHint(false);
      this.#flashStateIndicator(false);
    }, { signal });
    // JS-driven loop: when the video naturally ends and the operator
    // wanted it to loop, restart by seeking. We avoid the native `loop`
    // attribute because it makes HLS players re-fetch the manifest at
    // each loop boundary — which drops them back to the lowest bitrate.
    // Seeking-and-replaying reuses the already-buffered segments and
    // preserves whatever quality the adaptive ladder had ramped to.
    video.addEventListener('ended', () => {
      if (!this.hasAttribute('data-loop')) return;
      try {
        video.currentTime = 0;
        video.play().catch(() => {});
      } catch (_) { /* noop */ }
    }, { signal });
    video.addEventListener('volumechange', () => {
      this.isMuted = video.muted;
      this.#updateMuteUI();
    }, { signal });
    video.addEventListener('timeupdate', () => {
      if (!this.#isScrubbing) this.#updateProgressBar(video);
    }, { signal });

    // Detect if video has no audio track and hide mute controls.
    // Skip for the mini placeholder — it's silent by design, but we still
    // need the unmute button visible so the user can trigger the hydrate
    // swap to the full video.
    if (this.hasAttribute('autoplay') && !isMini) {
      this.#detectAudioPresence(video, signal);
    }
  }

  /**
   * Listens to native video element events to keep UI in sync
   * @param {AbortSignal} signal
   */
  #observeVideoElement(signal) {
    const video = this.querySelector('video');
    if (video) {
      this.#attachVideoListeners(video);
    } else {
      const observer = new MutationObserver(() => {
        const v = this.querySelector('video');
        if (v) {
          this.#attachVideoListeners(v);
          observer.disconnect();
        }
      });
      observer.observe(this, { childList: true, subtree: true });
      signal.addEventListener('abort', () => observer.disconnect());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#miniListenersController?.abort();
    this.#miniListenersController = null;
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const btn = this.refs.playToggleButton;
    if (btn instanceof HTMLElement) {
      const playIcon = btn.querySelector('.video-controls__icon--play');
      const pauseIcon = btn.querySelector('.video-controls__icon--pause');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
      const label = isPlaying
        ? btn.dataset.pauseLabel || 'Pause video'
        : btn.dataset.playLabel || 'Play video';
      btn.setAttribute('aria-label', label);
    }
  }

  /**
   * Briefly flashes the play or pause icon in the center of the video
   * @param {boolean} isPlaying - Whether the video is now playing
   */
  #flashStateIndicator(isPlaying) {
    const indicator = this.refs.videoStateIndicator;
    if (!indicator) return;

    if (!this.#userInitiated) return;
    this.#userInitiated = false;

    indicator.classList.remove('is-visible', 'is-playing', 'is-paused');

    // Force reflow so the animation restarts
    void indicator.offsetWidth;

    indicator.classList.add('is-visible', isPlaying ? 'is-playing' : 'is-paused');

    // Clean up the flash classes once the animation completes so the
    // persistent paused-state UI (CSS scoped with `:not(.is-visible)`)
    // can take over — replaces the briefly-flashed pause icon with the
    // persistent play icon while the video stays paused.
    const onEnd = () => {
      indicator.classList.remove('is-visible', 'is-playing', 'is-paused');
      indicator.removeEventListener('animationend', onEnd);
    };
    indicator.addEventListener('animationend', onEnd, { once: true });
    // Safety fallback if animationend is missed (e.g., DOM mutation,
    // tab backgrounded mid-animation): cleanup matches the keyframe
    // duration (0.8s) plus a small buffer.
    setTimeout(onEnd, 1000);
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    // Mini-mode swap: the centered button is now the swap trigger.
    // Hydrates the full video and plays it unmuted from the start.
    if (this.#hydrateFullVideo()) {
      this.#claimAudioFocus();
      return;
    }

    // First-time load (click-to-play with template): legacy path.
    if (!this.getAttribute('data-media-loaded')) {
      this.loadContent(true);
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);

      // For custom-controls mode: user clicked play, so start
      // the video unmuted (user gesture allows this)
      if (this.hasAttribute('data-custom-controls')) {
        const video = this.querySelector('video');

        if (video) {
          video.muted = false;
          this.isMuted = false;
          this.#updateMuteUI();
          video.play().catch(() => {});
        }
      }
      return;
    }

    // Already loaded: the poster button is now also the resume affordance
    // for any paused video (sitewide). Treat this as a user-initiated play.
    this.#userInitiated = true;
    if (!this.isPlaying) {
      this.playMedia(true);
      this.#claimAudioFocus();
    }
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    // For autoplay deferred-media, defer poster fade to first 'playing'
    // event (handled in #observeVideoElement). Non-autoplay (click-to-play)
    // hides the poster immediately for click feedback, since the user
    // gesture context typically lets the video render quickly.
    if (!this.hasAttribute('autoplay')) {
      this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    }

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // Force-kick playback for Safari (which sometimes ignores the autoplay
      // attribute when the element is appended dynamically). Silence any
      // rejection — playMedia will re-attempt, and AbortError just means
      // pauseMedia raced ahead of us (e.g. user already swiped past).
      content.play().catch(() => {});
    }
  }

  /**
   * Mini-mode swap: hydrate the full <video> from <template data-full-video>,
   * play it unmuted from the start, and fade the mini placeholder out once
   * the full has rendered its first frame. Returns true if hydration ran.
   * Idempotent: subsequent calls return false (template is removed once
   * hydrated).
   *
   * Called by toggleMedia / toggleMute / unmuteAndRestart whenever the user
   * has signaled intent to actually engage with the video. Must be called
   * inside a user gesture so the unmuted play() isn't blocked by the
   * autoplay policy.
   *
   * @returns {boolean}
   */
  #hydrateFullVideo() {
    const fullTemplate = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector('template[data-full-video]')
    );

    // Prefer a previously-prewarmed full <video> (inserted by
    // card-carousel.js when the carousel was already in unmuted state).
    // It already has metadata + first chunk in the browser cache from
    // its preload="metadata" fetch, so promoting it skips the cold
    // first-byte wait that otherwise dominates Mom-Truths-style swaps.
    const prewarmed = /** @type {HTMLVideoElement | null} */ (
      this.querySelector('video[data-prewarm]')
    );

    let content;
    if (prewarmed) {
      // Promote the prewarm element to the live full video.
      prewarmed.removeAttribute('data-prewarm');
      prewarmed.style.removeProperty('display');
      prewarmed.setAttribute('preload', 'auto');
      prewarmed.muted = false;
      content = prewarmed;
      if (fullTemplate) fullTemplate.remove();
    } else {
      if (!fullTemplate) return false;
      const cloned = fullTemplate.content.firstElementChild?.cloneNode(true);
      if (!cloned) return false;
      fullTemplate.remove();
      // Strip `autoplay` so the browser doesn't kick off muted playback before
      // we set muted=false. We'll explicitly call play() inside the user gesture.
      if (cloned instanceof HTMLVideoElement) {
        cloned.removeAttribute('autoplay');
        cloned.muted = false;
      }
      content = cloned;
    }

    // Detach mini listeners BEFORE pausing the mini so its 'pause' event
    // doesn't flip isPlaying=false and flicker the play/pause UI. The mini
    // element stays in the DOM (visible) until the full video paints and
    // the fade transition completes.
    const mini = /** @type {HTMLElement | null} */ (
      this.querySelector('.deferred-media__mini-video')
    );
    if (this.#miniListenersController) {
      this.#miniListenersController.abort();
      this.#miniListenersController = null;
    }
    if (mini instanceof HTMLVideoElement) {
      mini.pause();
    }

    // Insert the full video BEFORE the mini in DOM order so generic lookups
    // like `querySelector('video')` (used by pauseMedia, #unmute, etc.)
    // return the full while the mini is fading. Visual stacking is governed
    // by CSS (mini is absolutely positioned on top), so DOM order is purely
    // a query-precedence concern.
    if (mini && mini.parentElement === this) {
      this.insertBefore(content, mini);
    } else {
      this.appendChild(content);
    }
    this.setAttribute('data-media-loaded', 'true');
    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    if (!(content instanceof HTMLVideoElement)) return true;

    // Listeners must be attached before play() so the 'playing' event that
    // triggers the mini fade is caught.
    this.#attachVideoListeners(content);

    // Fade the mini once the full has its first frame ON SCREEN AND
    // composed correctly (after iOS Safari's intrinsic-size first
    // frames). The canonical signal for that is the host's
    // [data-video-rendered] attribute, which #attachVideoListeners
    // sets only after the SECOND rVFC firing — see the !isMini branch
    // there for rationale (Apple devforums 707247). Triggering on the
    // full's first rVFC was too early: the full's bad intrinsic-size
    // frame would briefly show through as the mini began fading.
    // Now both flow through one signal, so the mini stays opaque for
    // the same window the poster does in non-mini-mode.
    if (mini) {
      const startFade = () => {
        this.setAttribute('data-mini-fading', '');

        const finalize = () => {
          mini.removeEventListener('transitionend', finalize);
          if (mini.isConnected) mini.remove();
          this.removeAttribute('data-mini-fading');
        };
        mini.addEventListener('transitionend', finalize, { once: true });

        // Belt-and-suspenders: if transitionend doesn't fire (unusual
        // browser quirk, mini already removed by another path), still
        // clean up so we don't leak the data-mini-fading attribute.
        setTimeout(() => {
          if (mini.isConnected || this.hasAttribute('data-mini-fading')) {
            finalize();
          }
        }, 1000);
      };

      // Observe [data-full-rendered] (NOT [data-video-rendered]).
      // [data-video-rendered] was already set when the mini's reveal
      // first ran, so observing it here would fire startFade
      // immediately — before the freshly-hydrated full has had its
      // own rVFC counter reach 2 frames. [data-full-rendered] is set
      // only by the full video's second rVFC firing, which is the
      // exact "the full is composed correctly" signal we need.
      if (this.hasAttribute('data-full-rendered')) {
        // Rare race — full's rVFC reveal beat us here. Start fade.
        startFade();
      } else {
        const observer = new MutationObserver(() => {
          if (this.hasAttribute('data-full-rendered')) {
            observer.disconnect();
            startFade();
          }
        });
        observer.observe(this, { attributes: true, attributeFilter: ['data-full-rendered'] });
        // Belt-and-suspenders: if rVFC never reaches 2 firings (tab
        // backgrounded mid-load, video stalls indefinitely), still
        // fade the mini after a generous timeout so the user doesn't
        // see a stuck mini placeholder.
        setTimeout(() => {
          observer.disconnect();
          if (mini.isConnected && !this.hasAttribute('data-mini-fading')) {
            startFade();
          }
        }, 5000);
      }
    }

    this.isMuted = false;
    this.isPlaying = true;
    this.#updateMuteUI();
    this.updatePlayPauseHint(true);

    // Propagate unmuted state to carousel/gallery siblings — sets the
    // container's data-unmuted attribute so future slide activations route
    // through playMedia's mini→full hydrate path (see playMedia below).
    this.#syncCarouselMuteState(false);

    // If we're inside a card-carousel, kick off metadata prewarm of the
    // adjacent slides' full videos NOW (carousel data-unmuted is set
    // above). Without this nudge the first post-unmute slide navigation
    // would still hit a cold cache — subsequent navigations are covered
    // by card-carousel.js's own slideChange handler.
    const carousel = this.closest('card-carousel');
    if (carousel && typeof /** @type {any} */ (carousel).prewarmCurrentAdjacent === 'function') {
      /** @type {any} */ (carousel).prewarmCurrentAdjacent();
    }

    const playPromise = content.play();
    if (playPromise) {
      playPromise.catch((err) => {
        if (err && err.name === 'NotAllowedError') {
          // Autoplay policy blocked the unmuted play despite the gesture
          // (rare — happens on some mobile browsers when the gesture chain
          // is broken by intermediate awaits). Fall back to muted so the
          // visual continues; user can hit unmute to retry.
          content.muted = true;
          this.isMuted = true;
          this.#updateMuteUI();
          content.play().catch(() => {});
        }
      });
    }

    return true;
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    this.#userInitiated = true;
    if (this.isPlaying) {
      this.pauseMedia();
      return;
    }
    // Manual play implies "I want to engage with this video" — in mini mode
    // that means swap to the full video and play unmuted. The user gesture
    // here authorizes the unmuted play().
    this.#centerMuteDismissed = true;
    if (this.#hydrateFullVideo()) {
      this.#claimAudioFocus();
      return;
    }
    // Manual play always unmutes — user gesture context allows it
    if (this.isMuted) {
      this.#unmute();
    }
    this.playMedia(true);
    this.#claimAudioFocus();
  }

  /**
   * Toggles mute/unmute state of the media. Unmute always implies "I want
   * to hear this video", so it also resumes playback when paused — that's
   * the user-facing contract for both the centered unmute button and the
   * corner mute/unmute toggle. Re-mute leaves playback state alone.
   */
  toggleMute() {
    if (this.isMuted) {
      this.#userInitiated = true;
      // Mini-mode: unmute means hydrate the full video — the mini placeholder
      // has no audio to surface.
      if (this.#hydrateFullVideo()) {
        this.#claimAudioFocus();
        return;
      }
      this.#unmute();
      if (!this.isPlaying) this.playMedia(true);
      this.#claimAudioFocus();
    } else {
      this.#mute();
    }
  }

  /**
   * Notifies every other deferred-media on the page that this one now
   * holds audio focus. Listeners (in connectedCallback) mute themselves
   * if they're in a different mute-group, so the user only ever hears
   * one video at a time across the whole page. Same-group siblings are
   * already coordinated via the carousel/gallery `data-unmuted` flag.
   */
  #claimAudioFocus() {
    document.dispatchEvent(new CustomEvent('media:audio-focus', {
      bubbles: false,
      detail: { source: this },
    }));
  }

  /**
   * Handles click anywhere on the deferred-media
   * - If muted and center button not dismissed: unmute and restart
   * - Otherwise: toggle play/pause
   * @param {Event} event
   */
  #handleVideoClick(event) {
    if (event.target?.closest('.deferred-media__poster-button, .video-controls, .video-progress, .video-mute-toggle')) return;

    // In a slideshow, only handle clicks on the visible (non-hidden) slide.
    const slideshowSlide = this.closest('slideshow-slide');
    if (slideshowSlide && slideshowSlide.hasAttribute('hidden')) return;

    // Inactive card-carousel slides set pointer-events:none on the
    // deferred-media itself (see _card-carousel-item.liquid), so this
    // handler doesn't fire on them — clicks fall through to the carousel.

    if (this.isMuted && !this.#centerMuteDismissed) {
      this.unmuteAndRestart();
    } else {
      this.toggleMedia();
    }
  }

  /**
   * Unmutes the media
   * @param {boolean} [syncCarousel=true] - Whether to sync mute state across the carousel
   */
  #unmute(syncCarousel = true) {
    /** @type {HTMLVideoElement | null} */
    const video = this.querySelector('video');
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (video) {
      video.muted = false;
    } else if (iframe) {
      iframe.dataset.muted = 'false';
      if (iframe.dataset.videoType === 'youtube') {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'unMute', args: '' }),
          '*'
        );
      } else {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ method: 'setVolume', value: 1 }),
          '*'
        );
      }
    }

    this.isMuted = false;
    this.#updateMuteUI();

    if (syncCarousel) this.#syncCarouselMuteState(false);
  }

  /**
   * Mutes the media
   * @param {boolean} [syncCarousel=true] - Whether to sync mute state across the carousel
   */
  #mute(syncCarousel = true) {
    /** @type {HTMLVideoElement | null} */
    const video = this.querySelector('video');
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (video) {
      video.muted = true;
    } else if (iframe) {
      iframe.dataset.muted = 'true';
      if (iframe.dataset.videoType === 'youtube') {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'mute', args: '' }),
          '*'
        );
      } else {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ method: 'setVolume', value: 0 }),
          '*'
        );
      }
    }

    this.isMuted = true;
    this.#updateMuteUI();

    if (syncCarousel) this.#syncCarouselMuteState(true);
  }

  /**
   * Syncs mute state across all deferred-media elements in the same
   * card carousel, including Swiper loop clones. Sets a data attribute
   * on the carousel so newly activated slides inherit the state.
   * @param {boolean} muted
   */
  #syncCarouselMuteState(muted) {
    const container = this.closest('card-carousel') || this.closest('media-gallery');
    if (!container) return;

    if (muted) {
      container.removeAttribute('data-unmuted');
    } else {
      container.setAttribute('data-unmuted', '');
    }

    // Apply to all sibling deferred-media elements (including loop clones)
    for (const media of container.querySelectorAll('deferred-media[autoplay]')) {
      if (media === this) continue;
      media.applyCarouselMuteState?.(muted);
    }
  }

  /**
   * Applies mute state without triggering carousel sync (prevents loops).
   * Called by sibling deferred-media elements during carousel-wide sync.
   *
   * If this slide's media hasn't hydrated yet (still in <template>), we
   * intentionally avoid touching isMuted — the container's data-unmuted
   * attribute is checked again in playMedia when the video is hydrated,
   * which is the authoritative point to apply mute state.
   * @param {boolean} muted
   */
  applyCarouselMuteState(muted) {
    // Always remember that the user dismissed the center prompt elsewhere
    // so the prompt doesn't re-appear when this slide later activates.
    if (!muted) this.#centerMuteDismissed = true;

    // Mini-mode siblings stay in mini mode — we don't hydrate the full
    // video on every carousel slide just because one slide unmuted. The
    // perf goal is "load the full only on direct user intent." If the user
    // navigates here and wants sound, they'll unmute this slide.
    if (this.querySelector('template[data-full-video]')) return;

    const hasMedia = this.querySelector('video, iframe[data-video-type]');
    if (!hasMedia) return;

    if (muted) {
      this.#mute(false);
    } else {
      this.#unmute(false);
    }
  }

  /**
   * Updates mute/unmute icon visibility and aria-labels
   */
  #updateMuteUI() {
    const btn = this.refs.muteToggleButton;
    if (btn) {
      const unmuteIcon = btn.querySelector('.video-controls__icon--unmute');
      const muteIcon = btn.querySelector('.video-controls__icon--mute');
      if (unmuteIcon) unmuteIcon.classList.toggle('hidden', !this.isMuted);
      if (muteIcon) muteIcon.classList.toggle('hidden', this.isMuted);
      btn.setAttribute('aria-label', this.isMuted
        ? this.dataset.unmuteLabel || 'Unmute video'
        : this.dataset.muteLabel || 'Mute video'
      );
    }

    const centerBtn = this.refs.centerMuteButton;
    if (centerBtn) {
      if (this.#centerMuteDismissed || !this.isMuted) {
        centerBtn.classList.add('hidden');
      }
    }
  }

  /**
   * Detects whether the video has an audio track.
   * Hides mute-related controls if no audio is found.
   * Uses browser-specific APIs: audioTracks (Safari),
   * mozHasAudio (Firefox), webkitAudioDecodedByteCount (Chrome/Safari).
   * @param {HTMLVideoElement} video
   * @param {AbortSignal} signal
   */
  #detectAudioPresence(video, signal) {
    let attempts = 0;
    const maxAttempts = 4;

    const check = () => {
      if (signal.aborted) return;

      // Pass if ANY indicator detects audio. Safari exposes audioTracks
      // but populates it lazily for HLS — falling through to
      // webkitAudioDecodedByteCount catches that case once decoding starts.
      let hasAudio = false;
      if (video.audioTracks && video.audioTracks.length > 0) hasAudio = true;
      if ('mozHasAudio' in video && /** @type {any} */ (video).mozHasAudio) hasAudio = true;
      if ('webkitAudioDecodedByteCount' in video
          && /** @type {any} */ (video).webkitAudioDecodedByteCount > 0) hasAudio = true;

      if (hasAudio) return;

      // HLS audio renditions can register late, especially when the
      // element starts muted. Retry before declaring no audio.
      attempts += 1;
      if (attempts < maxAttempts) {
        setTimeout(check, 1000);
        return;
      }

      this.#hideAudioControls();
    };

    video.addEventListener('playing', () => {
      setTimeout(check, 500);
    }, { once: true, signal });
  }

  /**
   * Hides mute-related controls when video has no audio track
   */
  #hideAudioControls() {
    this.setAttribute('data-no-audio', '');
    const muteBtn = this.refs.muteToggleButton;
    const centerBtn = this.refs.centerMuteButton;
    if (muteBtn) muteBtn.style.display = 'none';
    if (centerBtn) centerBtn.style.display = 'none';
  }

  /**
   * Handles the case where autoplay is completely blocked
   * by the device or browser (e.g. low power mode, data saver).
   */
  #handleAutoplayBlocked() {
    this.isPlaying = false;
    this.updatePlayPauseHint(false);
    this.setAttribute('data-autoplay-blocked', '');
    // Surface the centered play icon as the manual-start affordance
    // (autoplay videos don't render the legacy poster-icon — see
    // markup change in snippets/video.liquid). Single attribute, single
    // CSS rule. The user clicks anywhere on the deferred-media to
    // toggleMedia, which clears this on the resulting 'playing' event.
    this.setAttribute('data-show-play-icon', '');

    // Show the poster button so user can manually start playback
    this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');

    // Hide autoplay-only controls
    this.refs.centerMuteButton?.classList.add('hidden');

    // For background videos, hide all controls — no user interaction expected
    if (this.hasAttribute('data-background-video')) {
      const controls = this.querySelector('.video-controls');
      if (controls) controls.style.display = 'none';
    }
  }

  /**
   * Unmutes the video and restarts playback from the beginning
   */
  unmuteAndRestart() {
    this.#userInitiated = true;
    this.#centerMuteDismissed = true;

    // Mini-mode: hydrate the full video and let it play unmuted from the
    // start. The clone always begins at currentTime=0, so no explicit seek
    // is needed.
    if (this.#hydrateFullVideo()) {
      this.#claimAudioFocus();
      return;
    }

    /** @type {HTMLVideoElement | null} */
    const video = this.querySelector('video');

    if (video) {
      video.currentTime = 0;
      this.#updateProgressBar(video);
    }

    if (this.isMuted) {
      this.#unmute();
    }

    if (!this.isPlaying) {
      this.playMedia(true);
    }

    this.#claimAudioFocus();
  }

  /**
   * Updates the progress bar fill width based on current video time
   * @param {HTMLVideoElement} video
   */
  #updateProgressBar(video) {
    const fill = this.refs.videoProgressFill;
    if (!fill || !video.duration) return;
    const pct = (video.currentTime / video.duration) * 100;
    fill.style.width = `${pct}%`;

    const bar = this.refs.videoProgress;
    if (bar) {
      bar.setAttribute('aria-valuenow', String(Math.round(pct)));
    }
  }

  /**
   * Makes a button follow the cursor with smooth easing.
   * On mouse leave, the button eases back to center.
   * @param {HTMLElement} btn - The button element to animate
   * @param {AbortSignal} signal
   */
  #initCursorFollow(btn, signal) {
    if (!btn) return;
    if (matchMedia('(pointer: coarse)').matches) return;

    let currentX = 0;
    let currentY = 0;
    let targetX = 0;
    let targetY = 0;
    let isFollowing = false;
    let rafId = null;
    const lerp = 0.03;

    const tick = () => {
      const dx = targetX - currentX;
      const dy = targetY - currentY;

      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
        currentX = targetX;
        currentY = targetY;
        btn.style.setProperty('--cursor-x', `${currentX}px`);
        btn.style.setProperty('--cursor-y', `${currentY}px`);
        if (!isFollowing) {
          rafId = null;
          return;
        }
      } else {
        currentX += dx * lerp;
        currentY += dy * lerp;
        btn.style.setProperty('--cursor-x', `${currentX}px`);
        btn.style.setProperty('--cursor-y', `${currentY}px`);
      }

      rafId = requestAnimationFrame(tick);
    };

    const startLoop = () => {
      if (!rafId) rafId = requestAnimationFrame(tick);
    };

    this.addEventListener('mousemove', (/** @type {MouseEvent} */ e) => {
      if (btn.classList.contains('hidden')) return;

      const rect = this.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      targetX = e.clientX - centerX;
      targetY = e.clientY - centerY;

      isFollowing = true;
      startLoop();
    }, { signal });

    this.addEventListener('mouseleave', () => {
      isFollowing = false;
      targetX = 0;
      targetY = 0;
      startLoop();
    }, { signal });

    signal.addEventListener('abort', () => {
      if (rafId) cancelAnimationFrame(rafId);
    });
  }

  /**
   * Sets up click and drag-to-scrub on the progress bar
   * @param {AbortSignal} signal
   */
  #initProgressBar(signal) {
    const bar = this.refs.videoProgress;
    if (!bar) return;

    const getSeekTime = (/** @type {PointerEvent} */ event) => {
      const video = this.querySelector('video');
      if (!video?.duration) return 0;
      const rect = bar.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      return ratio * video.duration;
    };

    bar.addEventListener('click', (event) => {
      const video = this.querySelector('video');
      if (!video) return;
      video.currentTime = getSeekTime(event);
      this.#updateProgressBar(video);
    }, { signal });

    bar.addEventListener('keydown', (event) => {
      const video = this.querySelector('video');
      if (!video?.duration) return;

      const step = 5;
      let handled = false;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowUp':
          video.currentTime = Math.min(video.duration, video.currentTime + step);
          handled = true;
          break;
        case 'ArrowLeft':
        case 'ArrowDown':
          video.currentTime = Math.max(0, video.currentTime - step);
          handled = true;
          break;
        case 'Home':
          video.currentTime = 0;
          handled = true;
          break;
        case 'End':
          video.currentTime = video.duration;
          handled = true;
          break;
      }

      if (handled) {
        event.preventDefault();
        this.#updateProgressBar(video);
      }
    }, { signal });

    bar.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      this.#isScrubbing = true;
      bar.classList.add('is-scrubbing');
      bar.setPointerCapture(event.pointerId);

      const video = this.querySelector('video');
      if (!video) return;
      video.currentTime = getSeekTime(event);
      this.#updateProgressBar(video);

      const onMove = (/** @type {PointerEvent} */ e) => {
        if (!this.#isScrubbing) return;
        video.currentTime = getSeekTime(e);
        this.#updateProgressBar(video);
      };

      const onUp = () => {
        this.#isScrubbing = false;
        bar.classList.remove('is-scrubbing');
        bar.removeEventListener('pointermove', onMove);
        bar.removeEventListener('pointerup', onUp);
        bar.removeEventListener('pointercancel', onUp);
      };

      bar.addEventListener('pointermove', onMove);
      bar.addEventListener('pointerup', onUp);
      bar.addEventListener('pointercancel', onUp);
    }, { signal });
  }

  /**
   * Plays the media.
   * @param {boolean} [fromUserGesture=false] - True when triggered by a direct
   *   user click (toggleMedia, unmuteAndRestart). False when triggered by
   *   carousel auto-activation. Affects how NotAllowedError is handled.
   */
  playMedia(fromUserGesture = false) {
    // Carousel/gallery-wide mute state is the source of truth for newly
    // activated slides — local isMuted may be stale if applyCarouselMuteState
    // was called while this slide's video was still in its <template>.
    const container = this.closest('card-carousel') || this.closest('media-gallery');
    const containerWantsUnmute = container?.hasAttribute('data-unmuted') ?? false;

    // Mini-mode + carousel-already-unmuted: skip the mini and hydrate the
    // full video unmuted. Once the user has unmuted any slide in the
    // carousel, every subsequently activated slide should come up as full
    // unmuted to match the audible-by-default expectation. The user gesture
    // that triggered the activation (slide arrow click, swipe) carries
    // transient activation forward, so the unmuted play() call inside
    // hydrate is allowed.
    if (containerWantsUnmute && this.querySelector('template[data-full-video]')) {
      this.#hydrateFullVideo();
      return;
    }

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      // For iframes, apply unmute postMessage on every playMedia when the
      // container is unmuted: YouTube/Vimeo iframes always reload muted
      // (mute=1 in src), so the local isMuted state can't tell us whether
      // the iframe is actually muted post-load.
      if (containerWantsUnmute) {
        this.#centerMuteDismissed = true;
        this.#unmute(false);
      }
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      let video = this.querySelector('video');
      // Video-on-demand hydration: if the video is wrapped in a <template>
      // (defer_video pattern used by card-carousel), mount it now.
      if (!video && this.querySelector('template')) {
        this.loadContent(false);
        video = this.querySelector('video');
      }
      if (video) {
        // Reconcile JS state against actual video element state — applyCarouselMuteState
        // may have flipped this.isMuted while the video was still in <template>.
        this.isMuted = video.muted;

        // Restore the autoplay attribute. The card-carousel prewarm strips
        // it from cloned non-active videos so they don't start playing on
        // hydration; when the slide becomes active and we route through
        // playMedia, we need the full `autoplay muted playsinline` trio
        // back on the element so iOS Safari allows JS-initiated play().
        //
        // EXCEPT for the mini placeholder. Mini videos are always
        // muted+playsinline, so JS-initiated play() is allowed by iOS
        // Safari without the autoplay attribute. Leaving it on causes
        // Safari to re-trigger autoplay (which resets currentTime to 0)
        // whenever the mini's compositor layer is invalidated — which
        // happens on every Swiper snap because the ancestor
        // .cc-item__card is animating a `transform: scale()`. The user-
        // visible symptom was the mini playing for ~300ms (until the
        // first compositor invalidation mid-snap) and then restarting.
        const isMini = video.classList.contains('deferred-media__mini-video');
        if (!isMini && !video.hasAttribute('autoplay')) {
          video.setAttribute('autoplay', '');
        }

        // Apply desired mute state BEFORE play() so Safari's autoplay policy
        // evaluates against the correct muted value. Otherwise we get a race
        // where play() succeeds muted, then we set muted=false, and the UI
        // shows "unmuted" while audio is silent (Safari may not actually
        // reflect the unmute without a user gesture).
        if (containerWantsUnmute && video.muted) {
          this.#centerMuteDismissed = true;
          video.muted = false;
          this.isMuted = false;
        }
        this.#updateMuteUI();

        // Carousel: every fresh activation plays the video from the
        // beginning. The user navigated TO this slide to see it, not
        // to resume from wherever they paused last time.
        //
        // Gated on `video.paused` because playMedia is called more than
        // once per activation: synchronously from #handleScrollerClick
        // (which starts playback), and then AGAIN from
        // #syncActiveCarouselVideo dispatched by Swiper's slideChange
        // and slideChangeTransitionEnd events. Without the paused
        // guard, the second call seeks the already-playing mini back
        // to 0 — producing the "video starts then restarts ~300ms
        // later" symptom on every click-to-active. Genuine activations
        // (slide was paused while inactive, becoming active again)
        // still get the fresh-from-zero behavior because the video is
        // paused at that moment.
        if (this.closest('card-carousel') && video.paused) {
          try { video.currentTime = 0; } catch (_) { /* HAVE_NOTHING */ }
        }

        if (fromUserGesture && video.readyState >= 3) {
          // User-initiated resume: play directly so Safari allows
          // unmuted playback within the user gesture context.
          // Don't go through #loadAndPlay which would re-mute on failure.
          video.play().catch(() => {});
        } else {
          this.#loadAndPlay(video);
        }
      }
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Ensures a video is loaded before attempting to play.
   * Safari rejects play() on videos with preload="none" that haven't
   * buffered any data yet, so upgrade preload then retry on canplay.
   * @param {HTMLVideoElement} video
   */
  #loadAndPlay(video) {
    // Move the poster from the native attribute to a CSS background so:
    // 1. The poster shows as a fallback while the video buffers
    // 2. Pausing shows the last video frame (not the static poster)
    // 3. There's no blank flash between poster removal and first frame
    const posterUrl = video.getAttribute('poster');
    if (posterUrl) {
      video.removeAttribute('poster');
      video.style.backgroundImage = `url(${posterUrl})`;
      video.style.backgroundSize = 'cover';
      video.style.backgroundPosition = 'center';
      // Keep the background image set indefinitely. Video frames paint on
      // top of the box's CSS background, so once the first frame arrives
      // it visually replaces the poster — no flash. We previously cleared
      // the background on 'playing' but that caused a brief glimpse of the
      // card's element background between the poster button fading and
      // the first video frame painting (a race condition more pronounced
      // with HLS, where 'playing' can fire microseconds before the paint).
    }

    // Poster overlay is faded on first 'playing' event by #observeVideoElement.
    // Adding the class here would hide the poster while the video is still
    // buffering, leaving a blank gap before frames render.

    // One play() attempt — the browser handles its own load lifecycle.
    // We previously also re-attempted on 'canplay', but that double-call
    // could quietly resume a video the carousel had already paused.
    // The autoplay attribute (set by liquid for autoplay videos) means the
    // browser starts playback on its own once enough data is buffered, so
    // a single explicit play() is sufficient as a safety net.
    video.play().catch((err) => {
      if (err.name === 'NotAllowedError' && !video.muted) {
        // Safari blocks play() on unmuted videos (autoplay policy).
        // Re-mute and retry so playback isn't silently lost.
        video.muted = true;
        this.isMuted = true;
        this.#updateMuteUI();
        video.play().catch((retryErr) => {
          if (retryErr.name === 'NotAllowedError') {
            this.#handleAutoplayBlocked();
          }
        });
      } else if (err.name === 'NotAllowedError') {
        // Even muted autoplay blocked (low power mode, data saver, etc.)
        this.#handleAutoplayBlocked();
      }
    });
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;
    this.updatePlayPauseHint(this.isPlaying);
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
